import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';
import { firmInfo } from '../mock';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const isSpanish = location.pathname.startsWith('/es');

  // Get the current page path for language switching
  const getCurrentPagePath = () => {
    const path = location.pathname;
    if (path === '/' || path === '/es') return '/';
    if (path === '/firm' || path === '/es/firma') return '/firm';
    if (path === '/practice-areas' || path === '/es/areas-de-practica') return '/practice-areas';
    if (path === '/contact' || path === '/es/contacto') return '/contact';
    if (path === '/free-consultation' || path === '/es/consulta-gratuita') return '/free-consultation';
    if (path === '/privacy-policy' || path === '/es/politica-privacidad') return '/privacy-policy';
    if (path === '/terms-of-service' || path === '/es/terminos-servicio') return '/terms-of-service';
    return '/';
  };

  const getLanguageTogglePath = () => {
    const basePath = getCurrentPagePath();
    if (isSpanish) {
      return basePath; // Return to English version
    } else {
      // Go to Spanish version
      const spanishPaths = {
        '/': '/es',
        '/firm': '/es/firma',
        '/practice-areas': '/es/areas-de-practica',
        '/contact': '/es/contacto',
        '/free-consultation': '/es/consulta-gratuita',
        '/privacy-policy': '/es/politica-privacidad',
        '/terms-of-service': '/es/terminos-servicio'
      };
      return spanishPaths[basePath] || '/es';
    }
  };

  const navLinks = isSpanish ? [
    { path: '/es', label: 'Inicio' },
    { path: '/es/firma', label: 'La Firma' },
    { path: '/es/areas-de-practica', label: '\u00c1reas de Pr\u00e1ctica' },
    { path: '/es/contacto', label: 'Cont\u00e1ctenos' }
  ] : [
    { path: '/', label: 'Home' },
    { path: '/firm', label: 'The Firm' },
    { path: '/practice-areas', label: 'Practice Areas' },
    { path: '/contact', label: 'Contact Us' }
  ];

  return (
    <nav style={{
      background: 'rgba(26, 28, 27, 0.95)',
      backdropFilter: 'blur(10px)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      width: '100%',
      zIndex: 1000,
      borderBottom: '1px solid var(--border-light)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Logo */}
        <Link to="/" style={{ 
          textDecoration: 'none', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          flex: '0 0 auto'
        }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_lglaw-injury/artifacts/xihcrodg_Vector%20.png" 
            alt="LG Law Office" 
            style={{ height: '40px', width: 'auto' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ 
              color: 'var(--brand-primary)', 
              fontSize: 'clamp(1rem, 3vw, 1.25rem)', 
              fontWeight: '700', 
              lineHeight: '1',
              whiteSpace: 'nowrap'
            }}>
              {firmInfo.name}
            </div>
            <div style={{ 
              color: 'var(--text-secondary)', 
              fontSize: 'clamp(0.65rem, 2vw, 0.75rem)', 
              marginTop: '2px',
              whiteSpace: 'nowrap'
            }}>
              {firmInfo.tagline}
            </div>
          </div>
        </Link>

        {/* Right Side - Actions */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flex: '0 0 auto'
        }}>
          {/* Desktop Navigation */}
          <div className="desktop-nav" style={{ 
            display: 'none',
            gap: '24px', 
            alignItems: 'center' 
          }}>
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                style={{
                  color: 'var(--text-white)',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'color 0.3s ease',
                  position: 'relative',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--brand-primary)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-white)'}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Phone Icon */}
          <a href={`tel:${firmInfo.phone}`} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'var(--brand-primary)',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1rem'
          }}>
            <Phone size={20} />
            <span className="phone-number" style={{ display: 'none' }}>
              {firmInfo.phone}
            </span>
          </a>

          {/* Desktop CTA Button */}
          <Link
            to={isSpanish ? "/es/consulta-gratuita" : "/free-consultation"}
            className="btn-primary desktop-cta"
            style={{
              display: 'none',
              fontSize: '0.85rem',
              padding: '0.7em 1.2em',
              whiteSpace: 'nowrap'
            }}
          >
            {isSpanish ? "Consulta Gratuita" : "Free Consultation"}
          </Link>

          {/* Language Toggle Link - Desktop Only, After CTA */}
          <Link
            to={getLanguageTogglePath()}
            title={isSpanish ? "ENGLISH" : "ESPAÑOL"}
            className="desktop-lang-toggle"
            style={{
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '0.95rem',
              padding: '6px 12px',
              border: '2px solid var(--brand-primary)',
              borderRadius: '6px',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'var(--brand-primary)';
              e.target.style.color = 'var(--text-inverse)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = 'var(--brand-primary)';
            }}
          >
            {isSpanish ? "EN" : "ES"}
          </Link>

          {/* Language Toggle Link - Mobile, Before Menu */}
          <Link
            to={getLanguageTogglePath()}
            title={isSpanish ? "ENGLISH" : "ESPAÑOL"}
            className="mobile-lang-toggle"
            style={{
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '0.95rem',
              padding: '6px 12px',
              border: '2px solid var(--brand-primary)',
              borderRadius: '6px',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'var(--brand-primary)';
              e.target.style.color = 'var(--text-inverse)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = 'var(--brand-primary)';
            }}
          >
            {isSpanish ? "EN" : "ES"}
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="mobile-menu-toggle"
            style={{
              display: 'flex',
              background: 'transparent',
              border: 'none',
              color: 'var(--brand-primary)',
              cursor: 'pointer',
              padding: '8px',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="mobile-menu" style={{
          background: 'var(--bg-page)',
          padding: '20px',
          borderTop: '1px solid var(--border-light)',
          width: '100%'
        }}>
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMenuOpen(false)}
              style={{
                display: 'block',
                color: 'var(--text-white)',
                textDecoration: 'none',
                padding: '16px',
                fontSize: '1.1rem',
                fontWeight: '500',
                borderBottom: '1px solid var(--border-light)'
              }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to={isSpanish ? "/es/consulta-gratuita" : "/free-consultation"}
            onClick={() => setIsMenuOpen(false)}
            className="btn-primary"
            style={{
              width: '100%',
              marginTop: '16px',
              display: 'flex'
            }}
          >
            {isSpanish ? "Consulta Gratuita" : "Free Consultation"}
          </Link>
        </div>
      )}

      {/* Responsive CSS */}
      <style>{`
        @media (min-width: 769px) {
          .desktop-nav {
            display: flex !important;
          }
          .phone-number {
            display: inline !important;
          }
          .desktop-cta {
            display: inline-flex !important;
          }
          .desktop-lang-toggle {
            display: flex !important;
          }
          .mobile-lang-toggle {
            display: none !important;
          }
          .mobile-menu-toggle {
            display: none !important;
          }
          .mobile-menu {
            display: none !important;
          }
        }
        
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .desktop-cta {
            display: none !important;
          }
          .desktop-lang-toggle {
            display: none !important;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navigation;